import { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { userDataService } from '@services/userDataService';

const PRIVACY_CONTACT = 'privacy@afixt.com';

interface BlobErrorShape {
  response?: { status?: number };
}

/**
 * REV-491: data & privacy.
 *
 * Two jobs: hand the user their own record, and tell them the truth about
 * deletion.
 *
 * Deletion is deliberately NOT a button here. A departing user may have
 * attested ConformanceSnapshots, and the platform invariants require every
 * snapshot to carry a named human attestor (invariant 3) and to be immutable
 * once its AuditSession is locked (invariant 4). Until that conflict is
 * resolved, a "Delete account" control could only be disabled or dishonest —
 * and a disabled submit is exactly what the platform form rules forbid,
 * because it cannot explain itself. Prose plus a real contact route can.
 */
export default function DataPrivacyTab() {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await userDataService.getExport();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `revenant-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // The response is a blob, so the error body is a Blob too — the usual
      // apiErrorMessage() helper would read `.error` off it and get undefined.
      // Status is the only reliable signal here.
      const status = (err as BlobErrorShape)?.response?.status;
      setError(
        status === 429
          ? t(
              'settings.privacy.download.rateLimited',
              'You have requested several exports recently. Please try again in an hour.'
            )
          : t('settings.privacy.download.failed', 'We could not prepare your download.')
      );
    } finally {
      setDownloading(false);
    }
  }, [t]);

  const includes: string[] = [
    t('settings.privacy.download.includes.profile', 'Your name, email, and account preferences'),
    t(
      'settings.privacy.download.includes.memberships',
      'The organizations and teams you belong to, and your role in each'
    ),
    t('settings.privacy.download.includes.sessions', 'Sign-in history and active devices'),
    t('settings.privacy.download.includes.activity', 'A record of the actions you have taken'),
    t(
      'settings.privacy.download.includes.attestations',
      'Attestation notes you have written, and your decisions on findings'
    ),
  ];

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        {t('settings.privacy.title', 'Data & privacy')}
      </Typography>

      <Typography variant="h6" component="h3" sx={{ mt: 2, fontSize: '1rem' }}>
        {t('settings.privacy.download.title', 'Download your data')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t(
          'settings.privacy.download.description',
          'Get a copy of everything we hold about you, as a JSON file.'
        )}
      </Typography>

      <List dense sx={{ listStyleType: 'disc', pl: 3, py: 1 }}>
        {includes.map((item) => (
          <ListItem key={item} sx={{ display: 'list-item', px: 0, py: 0.25 }} disableGutters>
            <ListItemText
              primary={item}
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
          </ListItem>
        ))}
      </List>

      <Typography variant="body2" color="text.secondary">
        {t(
          'settings.privacy.download.scope',
          'This covers you, not your organization. It does not include your organization’s findings, scans, or reports — those belong to the organization and are exported from its own reporting pages.'
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={
            downloading ? (
              <CircularProgress size={18} color="inherit" aria-hidden="true" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleDownload}
          disabled={downloading}
          aria-busy={downloading}
        >
          {downloading
            ? t('settings.privacy.download.preparing', 'Preparing your download…')
            : t('settings.privacy.download.action', 'Download my data')}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" component="h3" sx={{ fontSize: '1rem' }}>
        {t('settings.privacy.deletion.title', 'Deleting your account')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t(
          'settings.privacy.deletion.description',
          'Account deletion is handled by our privacy team so that we can check what needs to happen to your organization’s records first.'
        )}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t(
          'settings.privacy.deletion.attestationWarning',
          'One thing to know before you ask: if you have signed off on any conformance attestation, your name stays attached to it. Those records are locked evidence and cannot be removed, because a conformance claim with no named person behind it is worthless. Everything else about your account can be erased.'
        )}
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Link
          href={`mailto:${PRIVACY_CONTACT}?subject=${encodeURIComponent('Account deletion request')}`}
        >
          {t('settings.privacy.deletion.action', 'Request account deletion')}
        </Link>
      </Box>
    </Paper>
  );
}
